import type { Dictionary } from 'lodash';
import moment, { Moment } from 'moment';
import { NO_ROUTE_ID, UNASSIGN_ROUTE } from '../../stores/routes-planner-store';
import type { RoutesPlannerRun, RoutesPlannerTask } from '../../types';
import { getStartOfDay } from '../../utils/date-utils';
import type { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';

export const orderTasksByDate = (
	tasks: RoutesPlannerTask[],
	routes: Dictionary<RoutesPlannerRun>,
	routesInfo: Dictionary<EtaResponseRouteItem>,
	teamTimeZone: string
): Map<string, Map<number, RoutesPlannerTask>> => {
	const tasksByDates = new Map();
	tasks.forEach(task => {
		const runId = task.run_id;
		if (runId === NO_ROUTE_ID) {
			if (tasksByDates.has(UNASSIGN_ROUTE)) {
				tasksByDates.get(UNASSIGN_ROUTE).set(task.id, task);
			} else {
				tasksByDates.set(UNASSIGN_ROUTE, new Map());
				tasksByDates.get(UNASSIGN_ROUTE).set(task.id, task);
			}
		} else {
			const routeDate = moment.unix(routesInfo[runId]?.depot_etl || routes[runId].depot_etl);
			const routeStartDay = getStartOfDay(routeDate, teamTimeZone);

			if (tasksByDates.has(routeStartDay)) {
				tasksByDates.get(routeStartDay).set(task.id, task);
			} else {
				tasksByDates.set(routeStartDay, new Map());
				tasksByDates.get(routeStartDay).set(task.id, task);
			}
		}
	});
	return tasksByDates;
};

export const assignTasksToDate = (
	tasks: RoutesPlannerTask[],
	tasksByDate: Map<string, Map<number, RoutesPlannerTask>>,
	timeZone: string
): Map<string, Map<number, RoutesPlannerTask>> => {
	tasks.forEach(task => {
		if (task.run_id !== NO_ROUTE_ID) {
			const taskNewDate = getStartOfDay(task.scheduled_at, timeZone);
			tasksByDate.get(taskNewDate).set(task.id, task);
		}
	});
	return tasksByDate;
};

export const orderRoutesByDate = (
	routes: Dictionary<RoutesPlannerRun>,
	timeZone: string
): Dictionary<RoutesPlannerRun[]> => {
	const runsByDates = {};
	Object.values(routes).forEach(route => {
		const routeDate = moment.unix(route.depot_etl);
		const routeStartDay = getStartOfDay(routeDate, timeZone);

		if (!runsByDates[routeStartDay]) {
			runsByDates[routeStartDay] = [route];
		} else {
			runsByDates[routeStartDay].push(route);
		}
	});
	return runsByDates;
};

const getUnassignedTasksInDate = (
	unassignedTasks: Map<number, RoutesPlannerTask>,
	presentedDate: Moment,
	teamTimeZone: string
): Map<number, RoutesPlannerTask> => {
	if (!unassignedTasks) {
		return new Map();
	}
	if (presentedDate) {
		const unassignedTasksInDate = Array.from(unassignedTasks.values()).filter(task => {
			const firstWP = task.way_points[0];
			const timeWindowStart = getStartOfDay(
				firstWP.no_earlier_than || firstWP.first_attempt_promise_no_earlier_than,
				teamTimeZone
			);
			const timeWindowEnd = moment(firstWP.no_later_than || firstWP.first_attempt_promise_no_later_than).endOf(
				'day'
			);
			return moment(presentedDate).isBetween(timeWindowStart, timeWindowEnd, 'day', '[]');
		});
		return new Map(unassignedTasksInDate.map(task => [task.id, task]));
	} else {
		return unassignedTasks;
	}
};

export const getTasksByIdsInPresentedDate = (
	tasksByDate: Map<string, Map<number, RoutesPlannerTask>>,
	presentedDate: Moment,
	teamTimeZone: string
): Map<number, RoutesPlannerTask> => {
	const tasksInDate: Map<number, RoutesPlannerTask> =
		tasksByDate.get(getStartOfDay(presentedDate, teamTimeZone)) || new Map();
	const unassignedTasksInDate = getUnassignedTasksInDate(
		tasksByDate.get(UNASSIGN_ROUTE),
		presentedDate,
		teamTimeZone
	);
	const taskByIdMap: Map<number, RoutesPlannerTask> = new Map([...tasksInDate, ...unassignedTasksInDate]);

	return taskByIdMap;
};
